import { useState, useEffect } from 'react';
import { Modal, Switch, Button, Space, Tag, Typography, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/use_api';

const { Text } = Typography;

function ConnectionEditModal({ open, onClose, conn, currentEntityId, isAdmin, canAcceptConnection = false, userId, onSuccess }) {
    const navigate = useNavigate();
    const api = useApi();
    const [saving, setSaving] = useState(false);
    const [connectedStatus, setConnectedStatus] = useState(false);
    const [isAdminRole, setIsAdminRole] = useState(false);
    const [windowRange, setWindowRange] = useState([null, null]);
    const [pauseUntil, setPauseUntil] = useState(null);
    const [pausing, setPausing] = useState(false);

    useEffect(() => {
        if (!open || !conn || !currentEntityId) return;

        setConnectedStatus(conn.connected_status);
        setIsAdminRole(conn.role === 'admin');
        setWindowRange([conn.starts_at ? dayjs(conn.starts_at) : null, conn.ends_at ? dayjs(conn.ends_at) : null]);
        setPauseUntil(null);
    }, [open, conn, currentEntityId]);  // re-run whenever the modal opens or a different conn is selected

    if (!conn) return null;

    const isInstall = conn.role === 'installed';
    const STATE_LABELS = { live: ['green', 'Live'], pending: ['orange', 'Pending'], paused: ['purple', 'Paused'], before: ['blue', 'Not started'], after: ['default', 'Ended'] };
    const [stateColor, stateLabel] = STATE_LABELS[conn.state] || STATE_LABELS[conn.connected_status ? 'live' : 'pending'];

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                // A term (todo D12): outside its window a connection counts as paused.
                starts_at: windowRange[0] ? windowRange[0].toISOString() : null,
                ends_at: windowRange[1] ? windowRange[1].toISOString() : null,
            };
            // An app's install keeps its role; it is never admin or member.
            if (!isInstall) payload.role = isAdminRole ? 'admin' : 'member';
            // Only send connected_status if the caller is allowed to flip it; this also
            // ensures self-accept attempts surface a clear backend error.
            if (canAcceptConnection) payload.connected_status = connectedStatus;

            await api.put(`/api/connections/${conn.connection_id}`, payload, {
                params: userId ? { user_id: userId } : undefined,
            });
            message.success('Connection updated');
            onSuccess?.();
            onClose();
        } catch (e) {
            message.error(e.message || 'Failed to update connection');
        } finally {
            setSaving(false);
        }
    };

    // Pause (todo D9): time-boxed, lifted by the end that paused it or a group admin.
    const pause = () => {
        setPausing(true);
        api.post(`/api/connections/${conn.connection_id}/pause`, { until: pauseUntil.toISOString() })
            .then(() => { message.success(`Paused until ${pauseUntil.format('D MMM YYYY')}`); onSuccess?.(); onClose(); })
            .catch(err => message.error(err.message || 'Could not pause'))
            .finally(() => setPausing(false));
    };
    const resume = () => {
        setPausing(true);
        api.post(`/api/connections/${conn.connection_id}/resume`)
            .then(() => { message.success('Resumed'); onSuccess?.(); onClose(); })
            .catch(err => message.error(err.message || 'Could not resume'))
            .finally(() => setPausing(false));
    };

    return (
        <Modal
            title={
                <Space>
                    <span>Connection — <strong>{conn.entity.name}</strong></span>
                    {conn.role && <Tag>{conn.role}</Tag>}
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button icon={<ExportOutlined />} onClick={() => navigate(`/entity/${conn.entity.id}`)}>
                        Open entity
                    </Button>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        {isAdmin && (
                            <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
                        )}
                    </Space>
                </Space>
            }
            width={480}
        >
            {/* Connection status */}
            {isAdmin ? (
                <Space direction="vertical" style={{ width: '100%', marginBottom: 20 }} size={12}>
                    {canAcceptConnection ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>Connected</Text>
                            <Switch checked={connectedStatus} onChange={setConnectedStatus} />
                        </div>
                    ) : (
                        <div>
                            <Text type="secondary">Status: </Text>
                            <Tag color={conn.connected_status ? 'green' : 'orange'}>
                                {conn.connected_status ? 'Connected' : 'Pending'}
                            </Tag>
                        </div>
                    )}
                    {!isInstall && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>Admin</Text>
                            <Switch checked={isAdminRole} onChange={setIsAdminRole} />
                        </div>
                    )}
                    <div>
                        <Space style={{ marginBottom: 6 }}>
                            <Text strong>Time</Text>
                            <Tag color={stateColor}>{stateLabel}</Tag>
                            {conn.state === 'paused' && conn.paused_until && <Text type="secondary">until {dayjs(conn.paused_until).format('D MMM YYYY')}</Text>}
                        </Space>
                        <DatePicker.RangePicker
                            style={{ width: '100%' }}
                            allowEmpty={[true, true]}
                            value={windowRange}
                            onChange={v => setWindowRange(v || [null, null])}
                            placeholder={['From (open)', 'Until (open)']}
                        />
                        {conn.connected_status && (
                            conn.state === 'paused' ? (
                                <Button style={{ marginTop: 8 }} loading={pausing} onClick={resume}>Resume now</Button>
                            ) : (
                                <Space style={{ marginTop: 8 }}>
                                    <DatePicker
                                        placeholder="Pause until"
                                        value={pauseUntil}
                                        onChange={setPauseUntil}
                                        disabledDate={d => d && (d.isBefore(dayjs(), 'day') || d.isAfter(dayjs().add(365, 'day')))}
                                    />
                                    <Button disabled={!pauseUntil} loading={pausing} onClick={pause}>Pause</Button>
                                </Space>
                            )
                        )}
                    </div>
                </Space>
            ) : (
                <div style={{ marginBottom: 20 }}>
                    <Text type="secondary">Status: </Text>
                    <Tag color={stateColor}>{stateLabel}</Tag>
                </div>
            )}

        </Modal>
    );
}

export default ConnectionEditModal;
